import { Link } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ScanLine, ShieldAlert, Route, CloudRain, Wrench, Fuel, ParkingSquare, FileText,
  Check, Star, ArrowRight, Menu, X, ChevronDown, MessageCircle, Sparkles, Heart,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SocialShare } from "@/components/SocialShare";

const HERO = "https://images.unsplash.com/photo-1604447199408-9798f9f64f88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const features = [
  { icon: ScanLine, title: "AI Car Scan", text: "Snap a photo — know the problem." },
  { icon: ShieldAlert, title: "Safety & SOS", text: "One tap for help on the road." },
  { icon: Route, title: "Traffic", text: "Beat Lagos traffic, save time." },
  { icon: CloudRain, title: "Climate", text: "Rain & flood alerts before you drive." },
  { icon: Wrench, title: "Car Care", text: "Never miss a service again." },
  { icon: Fuel, title: "Fuel & Expenses", text: "See where your money dey go." },
  { icon: ParkingSquare, title: "Parking", text: "Find safe parking nearby." },
  { icon: FileText, title: "Documents", text: "Renew before it expires." },
];

const steps = [
  { n: "1", title: "Sign up free", text: "Phone or email — takes 30 seconds." },
  { n: "2", title: "Add your car", text: "Make, model, year, plate & mileage." },
  { n: "3", title: "Relax", text: "VecPadi watches your car every day." },
];

const testimonials = [
  { name: "Chidi O.", role: "Uber driver, Lagos", text: "VecPadi told me my brake pads needed changing before it became a big problem. Saved me plenty money." },
  { name: "Amaka N.", role: "Car owner, Lekki", text: "The rain and flood alert is real. It don save my car from Lekki flood twice already." },
  { name: "Bola A.", role: "Sales rep", text: "I no dey forget my insurance renewal again. The reminders just work. No wahala." },
];

const faqs = [
  { q: "Is VecPadi free?", a: "Yes! You get vehicle profile, basic reminders, expense tracking and a basic AI assistant for free. Premium unlocks AI photo scanning, unlimited assistant, advanced health scoring and more." },
  { q: "Does the AI replace a mechanic?", a: "No. VecPadi's visual AI is an advisory tool to help you understand issues early. Always confirm with a certified mechanic for repairs." },
  { q: "Which cars are supported?", a: "Any car! Just enter your make, model and year. VecPadi works for saloon cars, SUVs, buses and more." },
  { q: "How much is Premium?", a: "₦1,500/month or ₦15,000/year (save 2 months). Cancel anytime." },
];

export default function Landing() {
  const [menu, setMenu] = useState(false);
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-zinc-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-600">
            <a href="#features" className="hover:text-zinc-900 transition-colors">Features</a>
            <a href="#how" className="hover:text-zinc-900 transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-zinc-900 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-zinc-900 transition-colors">FAQ</a>
            <a href="https://climora.life" target="_blank" rel="noopener noreferrer" data-testid="nav-wedalytics" className="hover:text-emerald-600 transition-colors">Wedalytics</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login" className="rounded-full">Log in</Button></Link>
            <Link to="/register"><Button data-testid="nav-signup" className="rounded-full bg-emerald-500 hover:bg-emerald-600">Get Your VecPadi</Button></Link>
          </div>
          <button className="md:hidden" onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {menu ? <X /> : <Menu />}
          </button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-zinc-200 bg-white px-4 py-4 space-y-3">
            <a href="#features" onClick={() => setMenu(false)} className="block py-1 text-zinc-700">Features</a>
            <a href="#how" onClick={() => setMenu(false)} className="block py-1 text-zinc-700">How it works</a>
            <a href="#pricing" onClick={() => setMenu(false)} className="block py-1 text-zinc-700">Pricing</a>
            <a href="https://climora.life" target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-wedalytics" className="block py-1 font-medium text-emerald-600">Wedalytics</a>
            <Link to="/login" className="block"><Button variant="outline" className="w-full rounded-full">Log in</Button></Link>
            <Link to="/register" className="block"><Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600">Get Your VecPadi</Button></Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20">
          <div className="vp-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles size={14} /> Your AI Vehicle Padi
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Meet VecPadi — <span className="text-emerald-500">Your Vehicle Padi.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              One smart app to understand your car, stay safe, beat traffic, manage expenses and
              never miss an important vehicle renewal.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta" size="lg" className="rounded-full bg-emerald-500 hover:bg-emerald-600 gap-2 h-13 px-7 text-base">
                  Get Your VecPadi <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/register">
                <Button data-testid="hero-try-free" size="lg" variant="outline" className="rounded-full h-13 px-7 text-base border-zinc-300">
                  Try Free
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-zinc-500">Your car. Your safety. <span className="font-semibold text-emerald-600">No wahala.</span></p>
          </div>
          <div className="relative vp-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="overflow-hidden rounded-[2rem] border border-zinc-200 shadow-2xl shadow-emerald-500/10">
              <img src={HERO} alt="Happy driver using VecPadi" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-xl sm:-left-6">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500 text-white font-bold">92</div>
                <div>
                  <div className="text-xs text-zinc-400">Vehicle Health</div>
                  <div className="text-sm font-semibold text-emerald-600">Your car dey okay.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Everything your car needs</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">One app. Every wahala sorted.</h2>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="group rounded-3xl border border-zinc-200 bg-white p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-500 group-hover:text-white">
                <f.icon size={22} />
              </div>
              <h3 className="mt-4 font-display text-base font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-zinc-500">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">How it works</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Set up in under a minute</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {steps.map((s) => (
              <div key={s.n} className="relative rounded-3xl border border-zinc-200 p-7">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-500 font-display text-xl font-bold text-white">{s.n}</div>
                <h3 className="mt-5 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-1 text-sm text-zinc-500">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why VecPadi */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="rounded-[2rem] border border-zinc-200 bg-gradient-to-br from-emerald-50 to-white p-8 sm:p-12">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Why VecPadi?</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Save money", "Catch small problems before they become big, expensive repairs."],
              ["Stay safe", "Emergency SOS, flood alerts and safety guidance when you need it."],
              ["Beat stress", "Never forget an oil change, insurance or vehicle licence again."],
              ["Drive smart", "Traffic and climate intelligence for smoother Lagos journeys."],
            ].map(([t, d]) => (
              <div key={t}>
                <div className="mb-3 grid h-9 w-9 place-items-center rounded-xl bg-emerald-500 text-white"><Check size={18} /></div>
                <h3 className="font-display text-base font-semibold">{t}</h3>
                <p className="mt-1 text-sm text-zinc-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Loved by Lagos drivers</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-3xl border border-zinc-200 p-6">
                <div className="flex gap-0.5 text-amber-400">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-zinc-700">"{t.text}"</p>
                <div className="mt-5">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-zinc-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Simple pricing</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Start free. Upgrade when ready.</h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-[2rem] border border-zinc-200 bg-white p-8">
            <h3 className="font-display text-xl font-bold">Free</h3>
            <p className="mt-1 text-sm text-zinc-500">Everything to get started</p>
            <div className="mt-5 font-display text-4xl font-extrabold">₦0</div>
            <ul className="mt-6 space-y-3 text-sm">
              {["Vehicle profile", "Basic reminders", "Expense tracking", "Basic AI assistant", "Basic traffic & weather"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-zinc-700"><Check size={16} className="text-emerald-500" /> {x}</li>
              ))}
            </ul>
            <Link to="/register" className="mt-8 block"><Button variant="outline" className="w-full rounded-full border-zinc-300">Try Free</Button></Link>
          </div>
          <div className="relative rounded-[2rem] border-2 border-emerald-500 bg-zinc-950 p-8 text-white">
            <div className="absolute -top-3 right-6 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold">MOST POPULAR</div>
            <h3 className="font-display text-xl font-bold">Premium</h3>
            <p className="mt-1 text-sm text-zinc-400">For serious car owners</p>
            <div className="mt-5 flex items-end gap-2">
              <span className="font-display text-4xl font-extrabold">₦1,500</span>
              <span className="mb-1 text-zinc-400">/month</span>
            </div>
            <p className="mt-1 text-xs text-emerald-400">or ₦15,000/year — save 2 months</p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Unlimited AI assistant", "AI vehicle photo scanning", "Advanced health score", "Climate & flood alerts", "Priority roadside assistance", "Family / multiple vehicles"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-zinc-200"><Check size={16} className="text-emerald-400" /> {x}</li>
              ))}
            </ul>
            <Link to="/register" className="mt-8 block"><Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600">Get Premium</Button></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-center font-display text-3xl font-bold tracking-tight sm:text-4xl">Questions? No wahala.</h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-zinc-200">
                <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="rounded-[2rem] bg-emerald-500 p-10 text-center text-white sm:p-16">
          <MessageCircle className="mx-auto mb-4" size={36} />
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Ready to meet your Vehicle Padi?</h2>
          <p className="mx-auto mt-3 max-w-lg text-emerald-50">Join Lagos drivers who save money and stay safe with VecPadi.</p>
          <Link to="/register" className="mt-8 inline-block">
            <Button size="lg" className="rounded-full bg-white text-emerald-600 hover:bg-zinc-100 gap-2 h-13 px-8 text-base">
              Get Your VecPadi <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row">
            <div>
              <Logo />
              <p className="mt-3 max-w-xs text-sm text-zinc-500">Your car. Your safety. No wahala. Built for Nigerian drivers, starting with Lagos.</p>
            </div>
            <div className="flex gap-12 text-sm">
              <div className="space-y-2">
                <div className="font-semibold text-zinc-900">Product</div>
                <a href="#features" className="block text-zinc-500 hover:text-zinc-900">Features</a>
                <a href="#pricing" className="block text-zinc-500 hover:text-zinc-900">Pricing</a>
              </div>
              <div className="space-y-2">
                <div className="font-semibold text-zinc-900">Legal</div>
                <a href="#" className="block text-zinc-500 hover:text-zinc-900">Privacy Policy</a>
                <a href="#" className="block text-zinc-500 hover:text-zinc-900">Terms</a>
                <a href="#" className="block text-zinc-500 hover:text-zinc-900">Contact</a>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-5 border-t border-zinc-100 pt-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="text-xs text-zinc-400">
              © {new Date().getFullYear()} VecPadi. All rights reserved.
              <div className="mt-2 flex flex-wrap items-center gap-1 text-zinc-500">
                Powered &amp; developed with <Heart size={13} className="fill-red-500 text-red-500" /> by
                <span className="font-semibold text-zinc-700">Susttify Eco Innovation Ltd</span>
                &amp; <span className="font-semibold text-zinc-700">Jimtech Enterprise Solutions</span>
              </div>
            </div>
            <SocialShare />
          </div>
        </div>
      </footer>
    </div>
  );
}
